// eslint-disable-next-line
import React, { MouseEvent, useState, FC, useEffect, useMemo } from 'react';
import {
  AuthActions,
  AuthStateAgentInfo,
  selectAgentInfo,
  selectAllEntitiesWithPlaceHolder,
  selectLoggedUser,
  selectUserEntity,
} from 'store/modules/auth';
import {
  ChannelType,
  EntitiesForm,
  Entity,
  BusinessName,
  DashboardRoutePath,
} from 'core/entities';
import { Formik, FormikProps } from 'formik';

import AppBar from '@material-ui/core/AppBar';
import Button from '@material-ui/core/Button';

import DialogContentText from '@material-ui/core/DialogContentText';
import Flag from 'components/Flag';
import Modal from 'components/Modal';
import Yup from 'config/yup-locale';
import { disableSubmit } from 'components/forms';
import { regexNumbersWithoutFirstCero } from 'utils/helpers';
import { selectCountryCode } from 'store/modules/config';
import { useAppDispatch } from 'store';
import { useHistory } from 'react-router';
import { useSelector } from 'react-redux';
import Box from '@material-ui/core/Box';
import { useGetSellerRole, useUserRole } from 'core/auth/hooks';
import { Roles } from 'core/auth';
import { useGetEmployeeInformation } from 'core/employee';
import dictionary from 'utils/dictionary';
import {
  StyledTabs,
  StyledTab,
  StyledMenuContainer,
  StyledMenuProfileName,
  Logo,
  Toolbar,
  StyledRightMenu,
} from './Header.styles';
import HeaderMenu from './HeaderMenu';
import ExitToApp from '@material-ui/icons/ExitToApp';
import { Tooltip } from '@material-ui/core';
import pdfIcon from 'assets/images/pdf_icon_white.svg';

export const validationSchema = (entities: Entity[]) =>
  Yup.object({
    entity: Yup.string().required(
      'Debe seleccionar entidad para poder continuar'
    ),
    dni: Yup.string()
      .trim()
      .matches(
        regexNumbersWithoutFirstCero,
        'El documento no debe comenzar con 0'
      )
      .min(
        5,
        'El documento debe tener al menos 5 números. Por favor reintente.'
      )
      .max(8)
      .when('entity', (entityID, schema) => {
        return entities.find(
          ({ id, channelType }) =>
            id === entityID && channelType === ChannelType.Indirect
        )
          ? schema.required('Debe completar el documento')
          : schema;
      }),
  });

const Header: FC = () => {
  const dispatch = useAppDispatch();
  const user = useSelector(selectLoggedUser);
  const sellerRole = useGetSellerRole([
    Roles.FIJASTH_PY_VENTAS,
    Roles.FIJASTH_PY_EXCEPCION,
  ]);
  // const showEmpleTab = useUserRole([sellerRole, Roles.FIJASTH_PY_EMPLE], true);
  const isSellerRole = useUserRole([sellerRole]);
  const claroTvRole = useUserRole([
    Roles.DTHSTH_PY_VENTAS,
    Roles.DTHSTH_PY_EXCEPCION,
  ]);
  const [showClaroTv, setShowClaroTv] = useState(false);
  const countryCode = useSelector(selectCountryCode);
  const authEntities = useSelector(selectAllEntitiesWithPlaceHolder);
  const selectedEntity = useSelector(selectUserEntity);
  const agentInfo = useSelector(selectAgentInfo);
  const history = useHistory();
  const APP_VERSION = process.env.REACT_APP_VERSION;
  // local state
  const [openLogoutModal, setOpenLogoutModal] = useState(false);
  const [openEntitiesModal, setOpenEntitiesDialog] = useState(false);

  const { data: employeeInformation } = useGetEmployeeInformation('');
  // inside useGetEmployeeInformation() getCurrentTab(history.location.pathname) === 2 ? user?.userName : ''

  const indirectEntities = useMemo(() => {
    return authEntities.filter(
      (ent) => ent.channelType === ChannelType.Indirect
    );
  }, [authEntities]);

  const showSelectEntityMenu = indirectEntities.length > 1;

  const entityFormInitialValues = {
    entity: selectedEntity?.id,
    dni: agentInfo?.documentNumber ?? '',
  };

  function getCurrentTab(path) {
    switch (path) {
      case '/dashboard/': {
        return 0;
      }
      case '/claro-tv': {
        return isSellerRole ? 1 : 0;
      }
      case '/claro-tv/activation': {
        return isSellerRole ? 1 : 0;
      }
      // case '/dashboard/emple': {
      //   return showClaroTv ? 2 : 1;
      // }
      // case '/dashboard/emple/activation': {
      //   return showClaroTv ? 2 : 1;
      // }
      case '/pdf': {
        return showClaroTv && isSellerRole ? 2 : 1;
      }
      default:
        return 0;
    }
  }

  function redirectToDashboard(path: DashboardRoutePath | string): void {
    switch (path) {
      case DashboardRoutePath.ACTIVACIONES:
      case '/dashboard/activation': {
        history.push(DashboardRoutePath.ACTIVACIONES);
        break;
      }
      case DashboardRoutePath.CLARO_TV:
      case '/claro-tv/activation': {
        history.push(DashboardRoutePath.CLARO_TV);
        break;
      }
      default: {
        history.push(DashboardRoutePath.ACTIVACIONES);
        break;
      }
    }
  }

  const handleEntitiesFormSubmit = (values: typeof entityFormInitialValues) => {
    const updatedCiInfo: AuthStateAgentInfo = {
      ...agentInfo,
      documentNumber: values.dni,
    };
    if (values.entity) {
      dispatch(AuthActions.selectEntity(values.entity));
      dispatch(AuthActions.setAgentInfo(updatedCiInfo));
      redirectToDashboard(history.location.pathname);
      setOpenEntitiesDialog(false);
    }
  };

  const handleEntityFormCancel = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setOpenEntitiesDialog(false);
  };

  const onClickLogo = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (selectedEntity) {
      isSellerRole ? history.push('/dashboard') : history.push('/claro-tv');
    }
  };

  const handleLogoutModal = () => {
    setOpenLogoutModal(true);
  };

  const handleEntityChange = (
    props: FormikProps<typeof entityFormInitialValues>
  ) => {
    setOpenEntitiesDialog(true);
    props.resetForm();
  };

  const handleLogout = () => {
    setOpenLogoutModal(false);
    dispatch(AuthActions.logout());
  };

  const handleTabChange = (url) => () => history.push(url);

  useEffect(() => {
    // const emple = authEntities.find((entity) => entity.isEmployee === true);

    // const emple = authEntities.find((entity) => entity.isEmployee === true);
    const claroTv =
      selectedEntity &&
      selectedEntity.business &&
      [BusinessName.ALL, BusinessName.TV].includes(selectedEntity.business);

    setShowClaroTv(claroTvRole && !!claroTv);

    // if (!emple) return;

    // const notEmple = authEntities.find((entity) => entity.isEmployee === false);

    switch (history.location.pathname) {
      case '/dashboard':
        break;
      // case '/dashboard/emple':
      //   if (emple) {
      //     dispatch(AuthActions.selectEntity(emple.id));
      //   }
      //   break;
      default:
        return;
    }
  }, [
    authEntities,
    dispatch,
    claroTvRole,
    selectedEntity,
    history.location.pathname,
  ]);

  return (
    <AppBar position="static">
      <Toolbar>
        <StyledMenuContainer>
          <Button onClick={onClickLogo} data-testid="logo-btn">
            <Logo />
          </Button>
          <StyledMenuProfileName>versión {APP_VERSION}</StyledMenuProfileName>
        </StyledMenuContainer>
        {user && selectedEntity && (isSellerRole || claroTvRole) && (
          <StyledTabs value={getCurrentTab(history.location.pathname)}>
            {isSellerRole && (
              <StyledTab
                disableRipple
                label="ACTIVACIONES"
                onClick={handleTabChange('/dashboard')}
              />
            )}
            {showClaroTv && (
              <StyledTab
                disableRipple
                label="CLARO TV"
                onClick={handleTabChange('/claro-tv')}
              />
            )}
            {/**
             * showEmpleTab is the one who manages
             * the status of the employment tab
             * In paraguay it was defined that the 'emple' tab
             * will not be there, so the logic is not
             * eliminated and is deactivated until further notice
             */}
            {/* {false && (
              <StyledTab
                disableRipple
                label="EMPLE"
                onClick={handleTabChange('/dashboard/emple')}
              />
            )} */}
            {(isSellerRole || claroTvRole) && (
              <StyledTab
                disableRipple
                label={
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                  >
                    <img
                      src={pdfIcon}
                      alt="pdf"
                      style={{ height: 22, display: 'block' }}
                      data-testid="pdf-icon"
                    />
                  </div>
                }
                onClick={handleTabChange('/pdf')}
              />
            )}
          </StyledTabs>
        )}
        <StyledRightMenu>
          <Flag countryCode={countryCode} />
          {user && selectedEntity && (
            <>
              <StyledMenuContainer>
                <Formik
                  validationSchema={validationSchema(authEntities)}
                  initialValues={entityFormInitialValues}
                  validateOnBlur={false}
                  onSubmit={handleEntitiesFormSubmit}
                >
                  {(formikProps) => (
                    <>
                      <HeaderMenu
                        showSelectEntityMenu={showSelectEntityMenu}
                        changeEntity={() => handleEntityChange(formikProps)}
                        selectedEntity={selectedEntity}
                        user={user}
                        agentInfo={agentInfo}
                        employeeInformation={employeeInformation}
                        currentTab={getCurrentTab(history.location.pathname)}
                      />
                      <Modal
                        data-testid="changeentity-modal"
                        maxWidth="sm"
                        open={openEntitiesModal}
                        title={dictionary.header_changeEntityModal_title}
                        onOk={formikProps.submitForm}
                        onCancel={handleEntityFormCancel}
                        okButtonProps={{ disabled: disableSubmit(formikProps) }}
                      >
                        <DialogContentText>
                          {
                            dictionary.header_changeEntityModal_entity_seller_text
                          }
                        </DialogContentText>
                        <Box mt={2}>
                          <EntitiesForm
                            isModalDialog
                            data={authEntities}
                            showButtons={false}
                          />
                        </Box>
                      </Modal>
                    </>
                  )}
                </Formik>

                <Modal
                  data-testid="logout-modal"
                  open={openLogoutModal}
                  title={dictionary.header_logoutModal_title}
                  onOk={handleLogout}
                  onCancel={() => setOpenLogoutModal(false)}
                >
                  {dictionary.header_logoutModal_text}
                </Modal>
              </StyledMenuContainer>
              <Tooltip title={dictionary.header_menu_logout} placement="bottom">
                <ExitToApp
                  style={{ cursor: 'pointer' }}
                  onClick={handleLogoutModal}
                  data-testid="icon-logout"
                />
              </Tooltip>
            </>
          )}
        </StyledRightMenu>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
